import { GetServerSideProps } from 'next';
import { captureMessage } from '@sentry/nextjs';

import { sendCounterMetric } from 'utils/metrics';
import { CleanSanityArticleData } from 'lib/sanity/types';
import { getPreviewArticle, getPublishedArticle } from 'lib/sanity';
import { cleanSlug, isPreviewSecretValidForArticleSlug } from 'utils/url-helpers/url-helpers';
import Article, { ArticleProps } from 'components/Article';
import { getSharedPageData } from 'utils/shared-page-data';
import createVariantResolver from 'utils/remote-config/content/create-variant-resolver';
import { CMSDataRequest } from 'types/sanity-request';
import { assertIsVertical } from 'utils/type-guards';
import { getVerticalConfig, Vertical } from 'utils/vertical';
import assemblePath from 'utils/assemblePath';
import getAlternateLinksByMarketIds from 'utils/i18n/alternateLink';
import { NotFoundError } from 'utils/errors';
import { serverExceptionHandler } from 'utils/exceptionHandler';
import { DEFAULT_LOCALE } from 'utils/i18n/localeConfig';
import { getMarketIdFromMarket } from 'lib/sanity/helpers/marketHelper';
import { filterByMarket, isAvailableInMarkets } from 'lib/sanity/helpers/marketFilter';

const getRedirectUrl = (slugInUrl: string, correctSlug: string, market: string, vertical: Vertical) => {
  if (slugInUrl === correctSlug) {
    return null;
  }

  const verticalPath = getVerticalConfig(vertical).pathParam;
  // If the slug is outdated, we redirect to the new one
  return assemblePath(market, verticalPath, 'article', correctSlug);
};

const getCMSData = async ({
  articleSlug,
  market,
  isPreview,
  vertical,
}: CMSDataRequest): Promise<CleanSanityArticleData> => {
  const slug = articleSlug.toLowerCase();

  let article;
  if (isPreview) {
    article = await getPreviewArticle({ slug, market, vertical });
  } else {
    article = await getPublishedArticle({ slug, market, vertical });

    // TODO: Temporary! Remove when internal links are fixed in S4P content, and all locales have some content for every article
    if (article && !article.content && vertical === Vertical.PODCASTERS) {
      captureMessage('Failed to get article for slug in preferred locale for market, falling back to en-US', {
        tags: {
          slug,
          market,
        },
      });
      article = await getPublishedArticle({ slug, market, vertical, localeOverride: 'en-US' });
    }
  }

  if (!article?.content || !isAvailableInMarkets(article, market)) {
    throw new NotFoundError('404');
  }

  return article;
};

export const getServerSideProps: GetServerSideProps<ArticleProps> = async context => {
  const { req, query } = context;
  const market = query.market as string;
  const previewSecret = query.previewsecret as string;
  const variantKey = query.variant as string;
  let vertical: Vertical | undefined;

  try {
    const articleSlug = cleanSlug(query.articleslug as string);
    const isPreview =
      Boolean(previewSecret) && (await isPreviewSecretValidForArticleSlug(previewSecret, articleSlug.toLowerCase()));

    vertical = assertIsVertical(query.vertical);

    // GetSharedPageData takes a significant amount of time to resolve (due to remote config resolution)
    // To decrease request latency we want to run this in the background.
    const sharedPageDataPromise = getSharedPageData(context);

    const resolveVariant = createVariantResolver(vertical, market, articleSlug, {
      userId: req.headers['sp-internal-user-id'] as string | undefined,
      trackingId: req.headers['sp-internal-tracking-id'] as string,
      isPreview,
      variantKey,
      req,
    });

    const article = await getCMSData({ articleSlug, market, isPreview, vertical });

    const redirectUrl = getRedirectUrl(articleSlug, article.slugs[0], market, vertical);
    if (redirectUrl) {
      sendCounterMetric('article-redirect', { redirect: 'redirect', vertical });
      return {
        redirect: { destination: redirectUrl, permanent: false },
      };
    }
    sendCounterMetric('article-redirect', { redirect: 'no-redirect', vertical });

    const resolvedArticle = await resolveVariant(article);

    const sharedPageData = await sharedPageDataPromise;

    const alternateLinks = getAlternateLinksByMarketIds(vertical, article.availableMarkets, `article/${articleSlug}`);

    const showCommunityLink = sharedPageData.market.locale === DEFAULT_LOCALE && vertical === Vertical.CONSUMER;

    const { availableMarkets: _unused, ...articleWithoutMarkets } = resolvedArticle;

    // Used to reduce the size of the response
    const cleanedArticle = {
      ...articleWithoutMarkets,
      relatedArticles: filterByMarket(articleWithoutMarkets.relatedArticles, getMarketIdFromMarket(market)).map(
        ({ availableMarkets, ...relatedArticle }) => relatedArticle,
      ),
    };
    return {
      props: {
        article: cleanedArticle,
        articleSlug,
        mastheadData: cleanedArticle.masthead,
        sharedPageData,
        alternateLinks,
        showCommunityLink,
      },
    };
  } catch (error) {
    return serverExceptionHandler(error, 'article-page', { market, vertical });
  }
};

export default Article;
